import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Key,
  Clock,
  Users,
  ArrowRight,
  Star,
  Camera,
  Home
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const RealEstateCleaningPage = () => {
  const services = [
    {
      title: "Pre-Listing Deep Cleaning",
      description: "Get homes ready for listing photos and showings. Clean, presentation-ready properties that photograph well and show better."
    },
    {
      title: "Open House Preparation",
      description: "Last-minute cleaning before open houses and showings. Fast turnaround when you need a home looking its best."
    },
    {
      title: "Move-Out Cleaning for Sellers",
      description: "Post-move-out cleaning to prepare vacant homes for final showings, walk-throughs, and closing."
    },
    {
      title: "Rescue Cleaning",
      description: "Urgent cleaning for homes that need help fast—occupied listings that need a refresh, or properties showing poorly and needing intervention."
    }
  ];

  const whatWeClean = [
    "Kitchen (appliances, cabinets, counters, sinks)",
    "Bathrooms (tubs, showers, toilets, vanities)",
    "Living areas, bedrooms, and common spaces",
    "Windows, blinds, and windowsills",
    "Baseboards, doors, and light switches",
    "Floors (vacuum, mop, spot clean)",
    "Entry areas and curb appeal touch-ups",
    "Final walk-through before photos or showings"
  ];

  const whyRealtors = [
    "Fast response for last-minute requests",
    "Same team, consistent quality across your listings",
    "Direct communication with Bob and Heather",
    "Flexible scheduling around showings and closings",
    "15+ years of professional cleaning experience",
    "We understand what presentation-ready means"
  ];

  const faqs = [
    {
      question: "How quickly can you clean a listing before photos or open house?",
      answer: "Most pre-listing cleans can be scheduled within 24-48 hours. For urgent photography sessions or last-minute open houses, call us at 321-408-1347—we'll work with you to meet your timeline when possible."
    },
    {
      question: "Do you work with Realtors regularly?",
      answer: "Yes. Many of our clients are Realtors and sellers preparing homes for market. We're familiar with what listing-ready looks like and understand the timelines involved in real estate."
    },
    {
      question: "What's the difference between pre-listing cleaning and regular house cleaning?",
      answer: "Pre-listing cleaning focuses on presentation—making sure the home looks its absolute best for photos and showings. We pay extra attention to details that buyers and photographers notice: windows, baseboards, kitchens, bathrooms, and overall visual appeal."
    },
    {
      question: "Can you help with occupied listings?",
      answer: "Yes. If a seller is still living in the home, we can do a refresh cleaning before showings or open houses to make sure it looks its best. We're respectful of their space and work efficiently."
    },
    {
      question: "Can you accommodate last-minute requests?",
      answer: "We'll do our best to work with your timeline. Call us at 321-408-1347 and explain the situation—if our schedule allows, we'll help."
    },
    {
      question: "What if a home is in rough condition?",
      answer: "We'll assess the situation and be honest about what's needed. For heavily soiled properties or neglected homes, we provide deep cleaning or post-construction-level cleaning. We'll give you a realistic timeline and cost."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Real Estate & Listing Preparation Cleaning in Brevard County | Pierce Premier",
      description: "Pre-listing cleaning, open house prep, and real estate cleaning for Brevard County Realtors and sellers. Fast, reliable, presentation-ready. Call 321-408-1347.",
      url: "/services/commercial/real-estate-cleaning"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Commercial Cleaning", url: "/services/commercial" },
      { name: "Real Estate Cleaning" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "Real Estate & Listing Preparation Cleaning",
      description: "Professional pre-listing cleaning, open house preparation, and real estate cleaning services for Brevard County Realtors and home sellers."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="Real Estate & Listing Preparation Cleaning in Brevard County | Pierce Premier"
        description="Pre-listing cleaning, open house prep, and real estate cleaning for Brevard County Realtors and sellers. Fast, reliable, presentation-ready. Call 321-408-1347."
        canonical="/services/commercial/real-estate-cleaning"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Key className="w-5 h-5 text-purple-600" />
                  <Badge className="bg-purple-600 text-white">Realtor Partners</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Real Estate & Listing Preparation Cleaning in Brevard County
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Realtors and sellers need homes that show well—clean, presentable, and photo-ready. Pierce Premier provides pre-listing cleaning, open house preparation, and move-out cleaning for Brevard County real estate professionals who need reliable service on real estate timelines.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  We're Bob and Heather—a husband-and-wife team serving Brevard County Realtors and sellers. You'll work directly with us and have responsive communication when listings need fast turnaround. We've cleaned properties from Melbourne to Cocoa Beach—vacant homes after move-out, occupied listings before showings, and pre-photography prep.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                  >
                    Request Estimate
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80" 
                  alt="Real estate listing preparation and cleaning services in Brevard County, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Realtors Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Presentation-Ready Results</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We understand what listing photos and open houses demand. Homes are cleaned for visual appeal—kitchens sparkle, bathrooms shine, and spaces look their best for buyers and cameras.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Turnaround</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Real estate moves fast. We respond quickly to last-minute requests and work around your photography sessions, open houses, and closing timelines.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Direct Communication</h3>
                  <p className="text-gray-600 leading-relaxed">
                    You'll work directly with Bob and Heather. No phone trees, no intermediaries—just call or text. We're responsive and easy to coordinate with when timelines are tight.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience. We know what quality looks like and deliver consistent, professional results that help listings show better.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  How Real Estate Cleaning Works
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Contact & Scheduling</h3>
                      <p className="text-gray-600">Call or text us with the property address, condition, and timeline (photo shoot, open house, or closing date). We'll schedule a walkthrough or provide an estimate based on your description.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Cleaning & Preparation</h3>
                      <p className="text-gray-600">We clean the property with focus on presentation—making sure kitchens, bathrooms, floors, and high-visibility areas look their best for showings or photos.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Final Walkthrough & Confirmation</h3>
                      <p className="text-gray-600">We do a final check to make sure everything looks right, then confirm completion with you so the property is ready for your next step.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Real Estate Cleaning Services
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-purple-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What We Clean for Listings
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whatWeClean.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-purple-100">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Realtors Work With Us Repeatedly
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whyRealtors.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Service Areas
              </h2>
              <p className="text-gray-600">Serving Realtors and sellers throughout Brevard County</p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Viera", link: "/areas/viera" },
                { name: "Satellite Beach", link: "/areas/satellite-beach" },
                { name: "Indialantic", link: "/areas/indialantic" },
                { name: "Cocoa Beach", link: "/areas/cocoa-beach" },
                { name: "Palm Bay", link: "/areas/palm-bay" }
              ].map((city, idx) => (
                <Link key={idx} to={city.link} className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200 hover:border-purple-400 transition-colors text-center group">
                  <span className="text-purple-700 font-semibold group-hover:text-purple-900">{city.name}</span>
                  <ArrowRight className="w-4 h-4 inline-block ml-1 text-purple-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Realtors
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Related Services
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/services/move-in-move-out-cleaning" className="p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-purple-400 transition-colors group">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600">Move-In / Move-Out Cleaning</h3>
                <p className="text-gray-600 text-sm">Residential move-in and move-out cleaning services</p>
              </Link>
              <Link to="/services/deep-cleaning" className="p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-purple-400 transition-colors group">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600">Deep Cleaning Services</h3>
                <p className="text-gray-600 text-sm">Comprehensive deep cleaning for homes</p>
              </Link>
            </div>
          </div>
        </section>

        <CTASection 
          title="Need Your Listing Cleaned Before Photos or Showings?"
          description="Call 321-408-1347 or request an estimate. We'll work with your timeline to get the property presentation-ready."
          primaryButtonText="Request Listing Cleaning Estimate"
        />
      </div>
    </>
  );
};

export default RealEstateCleaningPage;
